// client/src/components/profile/connectedApps/useItAppItems.js
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import { useAuth } from '../../../context/AuthContext';

const opts = { withCredentials: true };
// 403 (flag off: requireFeature, or no permission) and 404 mean "this checklist
// is not available here", not "it failed": render nothing and leave the Google
// counter exactly as it was. Only other failures are a load error.
const UNAVAILABLE = [403, 404];
const sameItem = (a, b) => a.app_id === b.app_id && a.item_type === b.item_type;
const GROUPS = ['close_manually', 'remove_seat'];

/**
 * The Close manually / Remove paid seat checklist for a member's profile.
 * status: 'off' (not enabled, not available, or not applicable is decided by
 * the caller from data.applicable) | 'loading' | 'ready' | 'error'.
 * setItem is optimistic: the row moves at once and rolls back if the write
 * fails (the error is rethrown so the caller can toast).
 * verifyItem is NOT optimistic: the Odoo item closes only when the server has
 * confirmed with Odoo, so the row moves on the answer. Resolves to the
 * server's `verified` reason; rejects with the axios error otherwise.
 */
export default function useItAppItems({ userId, enabled }) {
  const base = `${API_BASE_URL}/api/profile/${userId}/it-app-items`;
  const auth = useAuth();
  const viewerName = (auth && auth.user && auth.user.name) || null;
  const [state, setState] = useState({ status: enabled ? 'loading' : 'off', data: null });
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  useEffect(() => {
    if (!enabled || !userId) { setState({ status: 'off', data: null }); return undefined; }
    let active = true;
    setState({ status: 'loading', data: null });
    axios.get(base, opts)
      .then((r) => {
        if (!active) return;
        const d = r && r.data;
        const ok = d && Array.isArray(d.close_manually) && Array.isArray(d.remove_seat);
        setState(ok ? { status: 'ready', data: d } : { status: 'error', data: null });
      })
      .catch((err) => {
        if (!active) return;
        const code = err && err.response && err.response.status;
        setState({ status: UNAVAILABLE.includes(code) ? 'off' : 'error', data: null });
      });
    return () => { active = false; };
  }, [base, enabled, userId]);

  const replaceItem = (next) => {
    if (!mounted.current) return;
    setState((s) => {
      if (!s.data) return s;
      const data = { ...s.data };
      GROUPS.forEach((g) => { data[g] = data[g].map((i) => (sameItem(i, next) ? next : i)); });
      return { ...s, data };
    });
  };

  const setItem = useCallback(async (item, status) => {
    replaceItem({
      ...item,
      resolution: status ? { status, closed_by_name: viewerName, closed_at: new Date().toISOString() } : null,
    });
    try {
      const url = `${base}/${item.app_id}/${item.item_type}`;
      const r = status ? await axios.put(url, { status }, opts) : await axios.delete(url, opts);
      if (r && r.data && r.data.item) replaceItem(r.data.item);
    } catch (err) {
      replaceItem(item);
      throw err;
    }
  }, [base, viewerName]);

  const verifyItem = useCallback(async (item) => {
    const r = await axios.post(`${base}/${item.app_id}/${item.item_type}/verify`, {}, opts);
    if (r && r.data && r.data.item) replaceItem(r.data.item);
    return r && r.data ? r.data.verified : null;
  }, [base]);

  return { status: state.status, data: state.data, setItem, verifyItem };
}
